import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {BackendResponse} from '../../http/backend-response/backend-response';
import {onFormQueryError} from '../../errors/on-form-query-error';
import {useNavigate} from '../../ui/navigation/use-navigate';
import {apiClient} from '../../http/query-client';
import {useAuth} from '../use-auth';
import {setBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useParams} from 'react-router-dom';
import {getFbc, getFbp} from '../../facebook/facebook-track';
import {getFromLocalStorage} from '@ui/utils/hooks/local-storage';

interface Response extends BackendResponse {
  bootstrapData?: string;
  message?: string;
  status: 'success' | 'needs_email_verification';
}

export interface RegisterPayload {
  email: string;
  password: string;
  password_confirmation: string;
  invite_id?: string;
  invite_type?: string;
}

export function useRegister(form: UseFormReturn<RegisterPayload>) {
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const {inviteId} = useParams();

  return useMutation({
    mutationFn: (payload: RegisterPayload) => {
      return register({
        invite_id: inviteId,
        ...payload,
      });
    },
    onSuccess: (response, payload) => {
      // Decode bootstrap data to extract registered user details (id, name, etc.)
      let registeredUser: any = null;
      try {
        if (response.bootstrapData) {
          const decoded = JSON.parse(response.bootstrapData);
          registeredUser = decoded.user;
        }
      } catch (e) {
        console.error('Failed to parse bootstrap data during registration:', e);
      }

      // Track Facebook Pixel + CAPI CompleteRegistration & Lead
      const emailVal = payload.email;
      const regEventId = 'reg_' + Math.random().toString(36).substring(2, 15);
      
      const hasFbq = typeof window !== 'undefined' && (window as any).fbq;
      const enrichedUserData = {
        em: emailVal,
        external_id: registeredUser ? String(registeredUser.id) : undefined,
        fn: registeredUser ? (registeredUser.name || '').trim().split(' ')[0] : undefined,
        ln: registeredUser ? ((registeredUser.name || '').trim().split(' ').length > 1 ? (registeredUser.name || '').trim().split(' ').slice(1).join(' ') : (registeredUser.name || '').trim().split(' ')[0]) : undefined,
        fbc: getFbc() || undefined,
        fbp: getFbp() || undefined,
        ph: (typeof window !== 'undefined' ? localStorage.getItem('checkout_phone') : null) || undefined,
      };

      if (hasFbq) {
        (window as any).fbq('track', 'CompleteRegistration', {}, { eventID: regEventId, ...enrichedUserData });
        (window as any).fbq('track', 'Lead', {}, { eventID: regEventId, ...enrichedUserData });
      }

      // Proxy to backend Conversions API
      apiClient.post('facebook/event', {
        event_name: 'CompleteRegistration',
        event_id: regEventId,
        user_data: enrichedUserData,
      }).catch(() => {});

      apiClient.post('facebook/event', {
        event_name: 'Lead',
        event_id: regEventId,
        user_data: enrichedUserData,
      }).catch(() => {});

      // Track Google Ads sign_up conversion event for AI Google Ads training algorithm
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'sign_up', {
          method: 'Email',
        });
      }

      // Check if there was an onboarding plan selected in localStorage / sessionStorage
      try {
        const onboardingPlan: any = getFromLocalStorage('be.onboarding.selected');
        if (onboardingPlan) {
          const selected = typeof onboardingPlan === 'string' ? JSON.parse(onboardingPlan) : onboardingPlan;
          const priceValue = selected.priceAmount ?? 0;
          const currencyValue = selected.currency ?? 'USD';

          if (hasFbq) {
            (window as any).fbq('track', 'StartTrial', {
              content_ids: [selected.productId.toString()],
              content_type: 'product',
              value: priceValue,
              currency: currencyValue,
            }, { eventID: regEventId, ...enrichedUserData });
          }
          apiClient.post('facebook/event', {
            event_name: 'StartTrial',
            event_id: regEventId,
            user_data: enrichedUserData,
            custom_data: {
              content_ids: [selected.productId.toString()],
              content_type: 'product',
              value: priceValue,
              currency: currencyValue,
            }
          }).catch(() => {});
        }
      } catch (e) {}

      setBootstrapData(response.bootstrapData!);
      if (response.status === 'needs_email_verification') {
        navigate('/');
      } else {
        navigate(getRedirectUri(), {replace: true});
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

function register(payload: RegisterPayload): Promise<Response> {
  return apiClient
    .post('auth/register', payload)
    .then(response => response.data);
}
